"use client";
import React, { useMemo } from "react";
import { motion, type MotionProps } from "motion/react";
import { cn } from "@/lib/utils";

export type TextShimmerProps = {
  children: string;
  as?: React.ElementType;
  className?: string;
  duration?: number;
  spread?: number;
  onClick?: () => void;
  style?: React.CSSProperties;
};

export type LoadingPlaceholderProps = {
  children: React.ReactNode;
  className?: string;
};

export function LoadingPlaceholder({
  children,
  className,
}: LoadingPlaceholderProps) {
  return (
    <div
      className={cn(
        "group/loading flex h-64 items-center justify-center gap-2 font-[450] transition-colors duration-300",
        "text-sidebar-muted-foreground hover:text-sidebar-accent-foreground",
        "[&_svg]:text-sidebar-icon-foreground hover:[&_svg]:text-sidebar-icon-accent-foreground",
        className
      )}
    >
      {children}
    </div>
  );
}

type ShimmerMotionComponent = React.ComponentType<Pick<TextShimmerProps, "className" | "onClick" | "style"> & { children?: string } & MotionProps & { translate?: "yes" | "no" }>;
const motionComponents = new Map<React.ElementType, ShimmerMotionComponent>();
function getMotionComponent(component: React.ElementType) {
  let cached = motionComponents.get(component);
  if (!cached) {
    cached = motion.create(component) as ShimmerMotionComponent;
    motionComponents.set(component, cached);
  }
  return cached;
}

function TextShimmerComponent({
  children,
  as: Component = "p",
  className,
  duration = 2,
  spread = 2,
  onClick,
  style,
}: TextShimmerProps) {
  // motion.create requires ElementType, Component is already typed as React.ElementType from props
  const MotionComponent = getMotionComponent(Component);

  const dynamicSpread = useMemo(() => {
    return children.length * spread;
  }, [children, spread]);

  return React.createElement(MotionComponent, {
    className: cn(
      "relative inline-block bg-[length:250%_100%,auto] bg-clip-text font-[450]",
      "text-transparent [--base-color:var(--sidebar-muted-foreground)] [--base-gradient-color:var(--sidebar-accent-foreground)]",
      "[background-repeat:no-repeat,padding-box] [--bg:linear-gradient(90deg,#0000_calc(50%-var(--spread)),var(--base-gradient-color),#0000_calc(50%+var(--spread)))]",
      "transition-[--base-color] duration-300 hover:[--base-color:var(--sidebar-accent-foreground)] group-hover/loading:[--base-color:var(--sidebar-accent-foreground)]",
      className,
    ),
    initial: { backgroundPosition: "100% center" },
    animate: { backgroundPosition: "0% center" },
    transition: { repeat: Infinity, duration, ease: "linear" },
    style: { "--spread": `${dynamicSpread}px`,
      backgroundImage: "var(--bg), linear-gradient(var(--base-color), var(--base-color))", ...style,
    } as React.CSSProperties,
    onClick,
    translate: "yes",
  }, children);
}

export const TextShimmer = React.memo(TextShimmerComponent);
